import Droit from '~/scripts/services/model/droit';
import Form from '~/scripts/services/model/form';
import Node from '~/scripts/services/model/node';
import DroitCust, { orders } from '~/scripts/services/model/droit-cust';
import DroitDef from '~/scripts/services/model/droit-def';
import Defaults from '~/scripts/constants/defaults';

/* globals Knitiv */

/**
 * @type {{
 *   hide: string,
 *   id: 'delete' | 'modify' | 'create' | 'read',
 *   class: string,
 *   value: number
 * }[]}
 */
export const rights = [
	{
		class: 'danger',
		hide:  'hideDelete',
		id:    'delete',
		value: 1,
	},
	{
		class: 'warning',
		hide:  'hideModify',
		id:    'modify',
		value: 2,
	},
	{
		class: 'success',
		hide:  'hideCreate',
		id:    'create',
		value: 4,
	},
	{
		class: 'info',
		hide:  'hideRead',
		id:    'read',
		value: 8,
	},
];

export default class DroitController {
	/**
	 * @typedef DroitTmp
	 * @property {import('@knitiv/api-client-javascript').KNode} [kid]
	 * @property {?('add' | 'clean' | 'del' | 'mod' | 'process')} order
	 * @property {{
	 *   isa: import('@knitiv/api-client-javascript').KNode | '0' | undefined,
	 *   isa_name: string,
	 *   kid: import('@knitiv/api-client-javascript').KNode | '0' | undefined,
	 *   name: string,
	 * }[] | undefined} org
	 * @property {{
	 *   isa: import('@knitiv/api-client-javascript').KNode | '0' | undefined,
	 *   isa_name: string,
	 *   kid: import('@knitiv/api-client-javascript').KNode | '0' | undefined,
	 *   name: string,
	 * }[] | undefined} role
	 * @property {?string} suborder
	 * @property {import('~/scripts/services/model/droit').TRight} tright
	 */

	/**
	 * @typedef DroitModel
	 * @property {any} [model]
	 * @property {DroitTmp} [new]
	 */

	/**
	 * @typedef DroitColumn
	 * @property {'droit' | 'droitcust' | 'droitdef'} id
	 * @property {Function} class
	 * @property {boolean} disabled
	 * @property {any[]} [groups]
	 * @property {'attr'|'org'} [mode]
	 * @property {string} name
	 * @property {(Droit | DroitCust | DroitDef)[]} nodeDroits
	 * @property {boolean} [hasRights]
	 * @property {any} [model]
	 * @property {string | undefined} [ooAdminForceProp]
	 * @property {string | undefined} [ooAdminForceTitle]
	 * @property {DroitModel} [tmp]
	 * @property {boolean} visible
	 */

	/* @ngInject */
	/**
	 * @param {Knitiv.RootScope} $rootScope
	 * @param {any} $scope
	 * @param {import('angular').ITimeoutService} $timeout
	 * @param {import('angular').translate.ITranslateService} $translate
	 * @param {any} NodeService
	 * @param {any} Notification
	 * @param {import('~/scripts/services/model/user').UserModel} User
	 */
	constructor(
		$rootScope,
		$scope,
		$timeout,
		$translate,
		NodeService,
		Notification,
		User,
	) {
		this.$rootScope = $rootScope;
		this.$scope = $scope;
		this.$timeout = $timeout;
		this.$translate = $translate;
		this.Notification = Notification;
		this.NodeService = NodeService;
		this.User = User;

		/** @type {Record<import('@knitiv/api-client-javascript').KNode, Node | undefined>} */
		this.facets = {};

		/** @type {Set<import('@knitiv/api-client-javascript').KNode>} */
		this.loadingKids = new Set();

		this.rights = rights;

		/** @type {DroitColumn[]} */
		this.blocks = [
			{
				id:                'droit',
				class:             Droit,
				disabled:          true,
				groups:            [],
				hasRights:         false,
				mode:              'org',
				model:             [],
				name:              'right.droit-title',
				nodeDroits:        [],
				ooAdminForceProp:  'droitForce',
				ooAdminForceTitle: 'Forcer les droits',
				visible:           true,
			},
			{
				id:         'droitcust',
				class:      DroitCust,
				disabled:   false,
				name:       'right.droitcust-title',
				nodeDroits: [],
				tmp:        {},
				visible:    true,
			},
			{
				id:         'droitdef',
				class:      DroitDef,
				disabled:   false,
				name:       'right.droitdef-title',
				nodeDroits: [],
				tmp:        {},
				visible:    (this.$rootScope.context?.role.mode
					&& [
						'ADMIN_SUPER',
						'ADMIN_SYS',
					].includes(this.$rootScope.context?.role.mode)) || false,
			},
		];

		this.blocks.map(block => {
			if (block.groups) {
				block.groups.push(
					{
						kid:       'K_NODE;KROLE',
						hasRights: false,
						mf:        [],
						order:     'NODE_LIST:K_NODE;KROLE,K_NODE;KPROFIL',
						type:      'role',
					},
				);
			}
			else {
				block.tmp = {
					new:   {
						order:    null,
						suborder: '',
						tright:    {
							delete: false,
							modify: false,
							create: false,
							read:   true,
						},
						org:      undefined,
						role:     undefined,
					},
				};
			}
			return block;
		});

		/** @type {boolean} */
		this.disabled = false;

		/** @type {Node[]} */
		this.droitorg = [];

		/** @type {any} */
		this.errors = {};

		/** @type {boolean} */
		this.fetching = false;

		/** @type {any} */
		this.form = undefined;

		/** @type {boolean} */
		this.hasCreatePermissions = $rootScope.hasCreatePermissions;

		/** @type {boolean} */
		this.hideRead = false;

		/** @type {boolean} */
		this.hideCreate = false;

		/** @type {boolean} */
		this.hideModify = false;

		/** @type {boolean} */
		this.hideDelete = false;

		/** @type {boolean} */
		this.isSuperAdmin = this.$rootScope.context?.role.mode === 'ADMIN_SUPER';

		/** @type {?import('angular').INgModelController} */
		this.ngModelCtrl = null;

		/** @type {?(import('~/scripts/services/model/node').default | import('~/scripts/services/model/form').default)} */
		this.node = null;

		/** @type {?Function} */
		this.onChange = null;

		/** @type {?import('../oo-admin/oo-admin.controller').default} */
		this.oo = null;

		/** @type {any} */
		this.orders = orders;

		/** @type {any} */
		this.tmp = {
			org: undefined,
		};
	}

	/**
	 * @param {any} block
	 * @param {any} group
	 * @param {any} item
	 */
	async addDroit(block, group, item) {
		// check for dups
		let pass = false;
		if (item && this.node) {
			pass = true;
			if (
				this.node.droit.some(droit => droit.constructor === block.class
				&& droit.role?.kid === item.kid && droit.org?.kid === group.kid)
			) {
				this.Notification.error({
					title:   this.$translate.instant('error.title'),
					message: this.$translate.instant('error.right-already-exists'),
				});
				pass = false;
			}
		}

		if (pass && this.node) {
			// @ts-ignore
			// eslint-disable-next-line new-cap
			const newDroit = new block.class();
			newDroit.kright = Defaults.DROIT.READ;
			newDroit.org.kid = group.kid;
			newDroit.role.kid = item.kid;
			newDroit.role.isa = item.isa;
			newDroit.role.name = item.name;

			if (this.node instanceof Form || this.node instanceof Node) {
				await (this.isDroitDef(newDroit) ? this.node.addDroitDef(newDroit) : this.node.addDroit(newDroit));
			}
			else {
				// @ts-ignore
				if (!('droit' in this.node) || !Array.isArray(this.node.droit)) {
					// @ts-ignore
					this.node.droit = [];
				}
				// @ts-ignore
				this.node.droit.push(newDroit);
			}

			if (this.form) {
				this.form.$setDirty();
			}
			this.ngModelCtrl?.$validate();
			this.$timeout(() => {
				group.mf = [];
				this.$scope.$apply();
				if (typeof this.onChange === 'function') {
					this.onChange();
				}
			});
		}
	}

	/**
	 * @param {DroitColumn} block
	 */
	async addDroitCD(block) {
		if (this.node) {
			/** @type {(DroitDef | DroitCust)} */
			// @ts-ignore
			// eslint-disable-next-line new-cap
			const newDroit = new block.class();
			newDroit.order = block.tmp?.new?.order || null;
			newDroit.suborder = block.tmp?.new?.suborder || '';

			if (block.tmp?.new?.org && block.tmp.new.org[0]) {
				newDroit.org.kid = block.tmp.new.org[0].kid;
				newDroit.org.isa = block.tmp.new.org[0].isa;
				newDroit.org.name = block.tmp.new.org[0].name;
			}

			if (block.tmp?.new?.role && block.tmp.new.role[0]) {
				newDroit.role.kid = block.tmp?.new?.role[0].kid;
				newDroit.role.isa = block.tmp?.new?.role[0].isa;
				newDroit.role.name = block.tmp?.new?.role[0].name;
			}

			newDroit.kright = 0;
			for (const r of this.rights) {
				if (block.tmp?.new && block.tmp.new.tright[r.id]) {
					newDroit.kright += r.value;
				}
			}

			if (this.node instanceof Form || this.node instanceof Node) {
				if (block.id === 'droitcust') {
					await this.node.addDroitCust(newDroit);
					if (typeof this.onChange === 'function') {
						this.onChange();
					}
				}
				else if (block.id === 'droitdef') {
					await this.node.addDroitDef(newDroit);
					if (typeof this.onChange === 'function') {
						this.onChange();
					}
				}
			}
			else if (block.id === 'droitcust') {
				// @ts-ignore
				if (!('droitcust' in this.node) || !Array.isArray(this.node.droitcust)) {
					// @ts-ignore
					this.node.droitcust = [];
					// @ts-ignore
					block.nodeDroits = this.node.droitcust;
				}
				// @ts-ignore
				this.node.droitcust.push(newDroit);
				if (typeof this.onChange === 'function') {
					this.onChange();
				}
			}
			else if (block.id === 'droitdef') {
				// @ts-ignore
				if (!('droitdef' in this.node) || !Array.isArray(this.node.droitdef)) {
					// @ts-ignore
					this.node.droitdef = [];
					// @ts-ignore
					block.nodeDroits = this.node.droitdef;
				}
				// @ts-ignore
				this.node.droitdef.push(newDroit);
				if (typeof this.onChange === 'function') {
					this.onChange();
				}
			}
			this.calculateIndexes();
			this.resetDroitCD(block);
		}
	}

	/**
	 * @param {any} block
	 * @param {any} item
	 * @param {boolean} [quiet]
	 */
	async addGroupOrg(block, item, quiet = false) {
		if (block.groups.some((/** @type {any} */ d) => d.kid === item.kid)) {
			if (quiet) {
				console.warn(this.$translate.instant('error.right-droitorg-already-exists'));
			}
			else {
				this.Notification.error({
					title:   this.$translate.instant('error.title'),
					message: this.$translate.instant('error.right-droitorg-already-exists'),
				});
			}
			return;
		}

		if (this.loadingKids.has(item.kid)) {
			return;
		}

		this.loadingKids.add(item.kid);

		try {
			const roots = [];
			const perimeters = [];

			let facet = this.facets[item.kid] ?? (item.link?.length > 0 && item);

			if (!facet) {
				this.facets[item.kid] = undefined;
				facet = await this.NodeService.load(item.kid, ['link']);
			}

			if (facet) {
				if (!(item.kid in this.facets) || this.facets[item.kid] === undefined) {
					this.facets[item.kid] = facet;
				}
				roots.push(...facet?.link.filter(
					(/** @type {import('~/scripts/services/model/link').default} */ link) => link.class_kid === 'K_NODE;ANY_CLASS'
						&& link.linker_kid === 'K_NODE;ROOT',
				).map(
					(/** @type {import('~/scripts/services/model/link').default} */ link) => link.objnum2_kid,
				) || []);
				perimeters.push(...facet?.link.filter(
					(/** @type {import('~/scripts/services/model/link').default} */ link) => link.class_kid === 'K_NODE;ANY_CLASS'
						&& link.linker_kid === 'K_NODE;KDROITORG',
				).map(
					(/** @type {import('~/scripts/services/model/link').default} */ link) => link.objnum2_kid,
				) || []);
			}
			else {
				console.warn('no facet', item.kid);
			}

			if (!block.groups.some((/** @type {any} */ d) => d.kid === item.kid) && (roots || perimeters)) {
				block.groups.push({
					kid:       item.kid,
					hasRights: false,
					name:      item.name || (item instanceof Node && item.getName()) || '',
					order:     `NODE_LIST:${[
						...roots,
						...perimeters,
						'K_NODE;KROLE',
					].join(',')}`,
					roots,
					perimeters,
					type:      'org',
				});
			}
		}
		finally {
			this.loadingKids.delete(item.kid);
		}

		this.$timeout(() => {
			this.tmp.org = undefined;
		});
	}

	/**
	 */
	calculateIndexes() {
		let index = 1;
		for (const cd of ['droitcust', 'droitdef']) {
			if (this.node) {
				// @ts-ignore
				for (const d of this.node[cd]) {
					d.index = index++;
				}
			}
		}
	}

	/**
	 * @param {DroitColumn} block
	 * @returns {boolean}
	 */
	checkAddDroitCD(block) {
		let valid = !!(block.tmp?.new?.order && block.tmp.new.suborder);
		if (valid) {
			const requires = block.tmp?.new?.order && block.tmp.new.suborder
				&& orders[block.tmp.new.order].sub[block.tmp.new.suborder].requires;

			if (block.tmp?.new && (
				(requires?.includes('role_kid') && (!block.tmp.new.role || block.tmp.new.role.length === 0))
					|| (requires?.includes('org_kid') && (!block.tmp.new.org || block.tmp.new.org.length === 0))
			)) {
				valid = false;
			}
		}
		return valid;
	}

	/**
	 * @param {Droit} droit
	 */
	deleteDroit(droit) {
		if (this.node instanceof Form || this.node instanceof Node) {
			this.node.delDroit(droit);
		}
		if (this.form) {
			this.form.$setDirty();
		}
		if (typeof this.onChange === 'function') {
			this.onChange();
		}
	}

	/**
	 * @param {DroitColumn} block
	 * @param {DroitCust | DroitDef} d
	 * @param {number} index
	 */
	deleteDroitCD(block, d, index) {
		if (this.node instanceof Form || this.node instanceof Node) {
			if (block.id === 'droitdef') {
				this.node.delDroitDef(d);
				if (d.kid === undefined) {
					this.node.droitdef.splice(index, 1);
				}
			}
			else if (block.id === 'droitcust') {
				this.node.delDroitCust(d);
				if (d.kid === undefined) {
					this.node.droitcust.splice(index, 1);
				}
			}
			this.calculateIndexes();
		}

		if (this.form) {
			this.form.$setDirty();
		}
		if (typeof this.onChange === 'function') {
			this.onChange();
		}
	}

	/**
	 * @param {DroitTmp} d
	 */
	droitCDUpdateOrder(d) {
		// set default value for suborder
		d.suborder = d.order ? this.orders[d.order].default : undefined;
		d.role = undefined;
		d.org = undefined;
		this.ngModelCtrl?.$validate();
		if (typeof this.onChange === 'function') {
			this.onChange();
		}
	}

	/**
	 * @param {DroitColumn} block
	 */
	droitCDUpdateSubOrder(block) {
		if (block.tmp?.new) {
			block.tmp.new.role = undefined;
			block.tmp.new.org = undefined;
		}
		this.ngModelCtrl?.$validate();
		if (typeof this.onChange === 'function') {
			this.onChange();
		}
	}

	async fetchDroitOrg() {
		if (!this.fetching) {
			this.droitorg.length = 0;
			this.fetching = true;
			const list = await this.NodeService.list('K_NODE;KDROITORG');
			if (list && list.length > 0) {
				this.$timeout(async () => {
					this.droitorg.push(...list);
					for (const block of this.blocks) {
						if (block.groups) {
							for (const droitorg of list) {
								await this.addGroupOrg(block, droitorg, true);
							}
						}
					}
				});
			}
			this.fetching = false;
		}
	}

	/**
	 * @returns {number}
	 */
	getColspan() {
		let nb = 1;

		if (!this.hideDelete) {
			nb++;
		}

		if (!this.hideModify) {
			nb++;
		}

		if (!this.hideCreate) {
			nb++;
		}

		if (!this.hideRead) {
			nb++;
		}

		if (!this.disabled) {
			nb++;
		}

		return nb;
	}

	/**
	 * @param {boolean} filterOrders
	 * @returns {any}
	 */
	getOrders(filterOrders) {
		return filterOrders ? Object.fromEntries(
			Object.entries(this.orders).filter(([id]) => this.isOrderEnabled(id)),
		) : this.orders;
	}

	/**
	 * @returns {boolean}
	 */
	hasErrors() {
		return Object.keys(this.errors).length > 0;
	}

	/**
	 * @param {DroitColumn} block
	 * @returns {Function}
	 */
	hasGroupRights(block) {
		return (/** @type {any} */ group) => {
			const hasRight = this.node?.droit.some(
				droit => (
					(this.disabled && (
						(block.id === 'droit' && !this.isDroitDef(droit))
						|| (block.id === 'droitdef' && this.isDroitDef(droit))
					)) || !this.disabled
				) && (
					(group.type === 'role' && droit.org.kid === 'K_NODE;KROLE')
					|| (group.type === 'org' && droit.org.kid === group.kid)
				),
			) || !this.disabled;

			// if (!group.hasRights) {
			group.hasRights = hasRight;
			// }

			if (!block.hasRights) {
				block.hasRights = hasRight;
			}

			return hasRight;
		};
	}

	/**
	 * @param {any} block
	 * @param {any} group
	 * @param {any} right
	 * @returns {boolean}
	 */
	hasGroupRightsAttr(block, group, right) {
		return this.node?.droit.some(droit => (
			((droit.kright & right.value) !== 0)
			&& (
				!group
				|| (group.type === 'role' && droit.role.isa === 'K_NODE;KROLE')
				|| (group.type === 'profil' && droit.role.isa === 'K_NODE;KPROFIL')
				|| (group.type === 'org' && droit.org.kid === group.kid
				)
			)
		),
		) || false;
	}

	/**
	 * @param {any} o
	 * @returns {boolean}
	 */
	isDroitDef(o) {
		return o.constructor === DroitDef;
	}

	/**
	 * @param {string} id
	 * @returns {boolean}
	 */
	isOrderEnabled(id) {
		if (id in orders) {
			const order = orders[id];
			return (
				order.restrict_admin
				&& this.$rootScope.context?.role?.mode
				&& ['ADMIN_SUPER', 'ADMIN_SYS'].includes(this.$rootScope.context?.role.mode)
			) || !order.restrict_admin;
		}
		return false;
	}

	async loadNode() {
		if (this.node) {
			for (const block of this.blocks) {
				for (const d of [...this.node.droit, ...this.node.droitcust, ...this.node.droitdef]) {
					if (
						d.constructor === block.class
						&& block.groups
						&& d.org.kid
						&& d.org.kid !== '0'
						&& block.groups
						&& !block.groups.some(
							(/** @type {Droit | DroitDef} */ d2) => d2.kid === d.org.kid,
						)
					) {
						await this.addGroupOrg(block, {
							kid:  d.org.kid,
							name: d.org.name,
						});
					}
				}
				switch (block.id) {
				case 'droitdef': {
					block.nodeDroits = this.node.droitdef;
					break;
				}
				case 'droitcust': {
					block.nodeDroits = this.node.droitcust;
					break;
				}
				default: {
					block.nodeDroits = this.node.droit;
					break;
				}
				}
			}
		}
		if (this.oo) {
			await this.fetchDroitOrg();
		}
	}

	/**
	 * @param {'AllObjectOfClass' | 'AllInherits' | 'AllObjectOfClassInherits' | 'self'} mode
	 */
	async permissionPopulate(mode) {
		const populate = await this.NodeService.permissionPopulate(this.node, mode);
		if (populate) {
			this.Notification.info({
				title:   this.$translate.instant('object.successfully-updated-title'),
				message: this.$translate.instant('object.successfully-updated-message'),
			});
		}
	}

	/**
	 * @param {DroitColumn} block
	 */
	resetDroitCD(block) {
		if (this.form) {
			this.form.$setDirty();
		}
		this.ngModelCtrl?.$validate();
		this.$timeout(() => {
			if (block.tmp) {
				block.tmp.new = {
					order:    null,
					suborder: '',
					tright:    {
						delete: false,
						modify: false,
						create: false,
						read:   true,
					},
					org:      undefined,
					role:     undefined,
				};
			}
			this.$scope.$apply();
			this.ngModelCtrl?.$validate();
		});
	}

	/**
	 * @param {any} block
	 * @param {Droit} droit
	 */
	updateDroit(block, droit) {
		// eslint-disable-next-line unicorn/no-reduce,unicorn/no-array-reduce
		droit.kright = 0;
		for (const r of this.rights) {
			if (droit.org.kid && droit.role.kid && block.model[droit.org.kid][droit.role.kid][r.id]) {
				droit.kright += r.value;
			}
		}
		if ((this.node instanceof Form || this.node instanceof Node) && droit.role.kid) {
			this.node.modDroit(droit.role.kid, droit.kright);
		}

		if (this.form) {
			this.form.$setDirty();
		}
		if (typeof this.onChange === 'function') {
			this.onChange();
		}
	}

	/**
	 * @param {DroitColumn} block
	 * @param {DroitCust | DroitDef} d
	 * @param {import('~/scripts/services/model/droit').TRight} [tright]
	 */
	updateDroitCD(block, d, tright = undefined) {
		// eslint-disable-next-line unicorn/no-reduce,unicorn/no-array-reduce
		if (tright !== undefined) {
			let kright = 0;
			for (const r of this.rights) {
				if (tright[r.id] === true) {
					kright += r.value;
				}
			}
			d.kright = kright;
		}
		if (d.kid && (this.node instanceof Form || this.node instanceof Node)) {
			this.$timeout(() => {
				if (block.id === 'droitcust') {
					this.node?.modDroitCust(d);
				}
				else if (block.id === 'droitdef') {
					this.node?.modDroitDef(d);
				}
			});
		}

		this.ngModelCtrl?.$validate();
		this.form?.$setDirty();

		if (typeof this.onChange === 'function') {
			this.onChange();
		}
	}

	/**
	 * @param {DroitColumn} block
	 * @param {number} index
	 */
	updateDroitCDIndex(block, index) {
		block.nodeDroits.splice(index, 1);
		block.nodeDroits.map((
			/** @type {Droit | DroitCust | DroitDef} */ d,
			/** @type {number} */ lineIndex,
		) => {
			if (this.node) {
				if (block.id === 'droitdef' && !(d instanceof DroitDef)) {
					// eslint-disable-next-line no-param-reassign
					d = new DroitDef(d);
					block.nodeDroits[lineIndex] = d;
				}
				else if (block.id === 'droitcust' && !(d instanceof DroitCust)) {
					// eslint-disable-next-line no-param-reassign
					d = new DroitCust(d);
					block.nodeDroits[lineIndex] = d;
				}

				if ((d.constructor === DroitCust || d.constructor === DroitDef) && index !== lineIndex) {
					if (d instanceof DroitDef) {
						d.index = index;
						this.node.modDroitDef(d);
					}
					else if (d instanceof DroitCust) {
						d.index = index;
						this.node.modDroitCust(d);
					}
					this.form?.$setDirty();
					if (typeof this.onChange === 'function') {
						this.onChange();
					}
				}
			}
			return d;
		});
		this.calculateIndexes();
		this.ngModelCtrl?.$validate();
	}

	/**
	 * @param {import('angular').IOnChangesObject} changes
	 */
	$onChanges(changes) {
		if (changes.disabled?.currentValue === false) {
			(async () => this.fetchDroitOrg())();
		}

		if (changes.node) {
			(async () => this.loadNode())();
		}
	}

	$onInit() {
		this.hideCreate = !!this.hideCreate;
		this.hideDelete = !!this.hideDelete;
		this.hideModify = !!this.hideModify;
		this.hideRead = !!this.hideRead;

		if (this.node && this.node instanceof Node) {
			(async () => this.loadNode())();
		}

		if (this.ngModelCtrl) {
			this.ngModelCtrl.$validators.checkDroit = (
			) => {
				let valid = true;
				if (this.node) {
					for (const /** @type {DroitCust|DroitDef} */ d of [
						...this.node.droitcust,
						...this.node.droitdef
					]) {
						if (!d.check()) {
							valid = false;
						}
					}
				}
				return valid;
			}

			this.ngModelCtrl.$validators.newDroitCD = () => {
				let valid = true;
				for (const block of this.blocks) {
					if (block.tmp?.new) {
						valid = valid && !(block.tmp.new.order || block.tmp.new.suborder);
					}
				}
				return valid;
			};
		}
	}
}
